import { LocationStrategy } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { RestoreVersionConfirmationDialogComponent } from '@components/restore-version-confirmation-dialog/restore-version-confirmation-dialog.component';
import { StartVersionDialogComponent } from '@components/start-version-dialog/start-version-dialog.component';
import { EnvironmentService } from '@service/environment.service';
import { ShipyardService } from '@service/shipyard.service';
import { VersionsHistoryService } from '@service/versions-history.service';
import { VesselService } from '@service/vessel.service';
import { RuntimeEnvironments } from '@src/app/models/runtime-enviroment.enum';
import { FOLDER_CREATED, PublishedVersion } from 'icreate/common/models/versions-history';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-versions-history',
    templateUrl: './versions-history.component.html',
    styleUrls: ['./versions-history.component.css'],
    standalone: false,
})
export class VersionsHistoryComponent implements OnInit, OnDestroy {
    destroySubscriptions$: Subject<void> = new Subject();

    vessel: Vessel;
    versionsList: PublishedVersion[];
    selectedVersion: PublishedVersion;
    previousVersion: PublishedVersion;
    iApiShipyardId: string;

    isLoading: boolean = false;

    urlState: any;

    private env: RuntimeEnvironments;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private shipyardService: ShipyardService,
        private versionsHistoryService: VersionsHistoryService,
        private location: LocationStrategy,
        private dialog: MatDialog,
        private vesselService: VesselService,
        private environmentService: EnvironmentService
    ) {
        this.env = this.environmentService.getEnvironment();
    }
    ngOnInit(): void {
        this.isLoading = true;
        if (this.env == RuntimeEnvironments.ELECTRON) {
            this.router.navigateByUrl('/home');
            return;
        }

        this.urlState = this.location.getState();
        this.route.params.pipe(takeUntil(this.destroySubscriptions$)).subscribe(async params => {
            const shipyardId = params['shipyardId'];
            const vesselId = params['vesselId'];
            const shipyardList = await this.shipyardService.getList();
            const selectedShipyard = shipyardList?.find(s => s.id === shipyardId);
            if (selectedShipyard) {
                this.vessel = selectedShipyard.vessels.find(vessel => vessel.id === vesselId);
                this.iApiShipyardId = selectedShipyard.iApiCompanyId;
            }

            if (!this.vessel) {
                this.router.navigateByUrl('home');
                return;
            }

            const publishedVersions = await this.versionsHistoryService.getVersionsList(
                this.iApiShipyardId,
                this.vessel.iAPIVesselId
            );

            this.versionsList = [...publishedVersions];

            this.versionsList.push({
                name: FOLDER_CREATED,
                publishedDate: this.vessel.creationDate,
            });

            if (this.vessel.projectStatus !== ProjectStatus.undefined) {
                this.versionsList.unshift({
                    name: this.vessel.projectVersion,
                    publishedDate: undefined,
                });
            }
            this.isLoading = false;
        });
    }

    ngOnDestroy(): void {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    handleVersionSelected(version) {
        this.previousVersion = this.getPreviousVersion(version);
        this.selectedVersion = version;
    }

    getPreviousVersion(selectedVersion): PublishedVersion {
        const indexSelectedVersion = this.versionsList.indexOf(selectedVersion);
        if (indexSelectedVersion !== -1) {
            if (indexSelectedVersion === this.versionsList.length - 1) {
                return null;
            } else {
                return this.versionsList[indexSelectedVersion + 1];
            }
        }
        return null;
    }

    async restoreVersion(version: PublishedVersion) {
        const dialogRef = this.dialog.open(RestoreVersionConfirmationDialogComponent, {
            width: '474px',
        });
        const restoreVersion = await lastValueFrom(dialogRef.afterClosed());
        if (restoreVersion) {
            const startDialogRef = this.dialog.open(StartVersionDialogComponent, {
                width: '474px',
                data: {
                    vessel: this.vessel,
                    shipyardIApi: this.iApiShipyardId,
                    inReferencePage: false,
                    referenceVersion: version.name,
                },
            });
            const newVersionInfo = await lastValueFrom(startDialogRef.afterClosed());
            if (newVersionInfo) {
                this.isLoading = true;
                let referenceVersion =
                    newVersionInfo.referenceVersion === this.vessel.projectVersion
                        ? undefined
                        : newVersionInfo.referenceVersion;
                this.vessel.ownerName = newVersionInfo.owner.name;
                this.vessel.ownerOid = newVersionInfo.owner.oid;
                this.vessel.projectVersion = newVersionInfo.versionName;
                this.vessel.locked = true;
                this.vessel.projectStatus = ProjectStatus.Editing;
                await this.vesselService.startVersion(this.vessel, this.iApiShipyardId, referenceVersion);
                this.isLoading = false;
                this.router.navigateByUrl(`home/${this.vessel.shipyardId}/${this.vessel.id}`);
            }
        }
    }
}
